import { AppConsts } from '@shared/AppConsts';
import { FilterMetadata, LazyLoadEvent, TableState } from 'primeng/api';
import { Paginator } from 'primeng/paginator';
import { Table } from 'primeng/table';
import { DateTime } from 'luxon';
import * as rtlDetect from 'rtl-detect';

export class PrimengTableHelper {
    predefinedRecordsCountPerPage = [ 10, 20, 50, 100, 250, 500];

    defaultRecordsCountPerPage = 20;

    isResponsive = true;

    resizableColumns: false;

    totalRecordsCount = 0;

    records: any[];

    selectedRecords: any[];

    isLoading = false;

    filter = {};

    showLoadingIndicator(): void {
        setTimeout(() => {
            this.isLoading = true;
        }, 0);
    }

    hideLoadingIndicator(): void {
        setTimeout(() => {
            this.isLoading = false;
        }, 0);
    }

    getSorting(table: Table): string {
        let sorting = '';

        if (table.sortMode === 'multiple') {
            if (table.multiSortMeta) {
                for (let i = 0; i < table.multiSortMeta.length; i++) {
                    const element = table.multiSortMeta[i];
                    if (i > 0) {
                        sorting += ',';
                    }
                    sorting += element.field;
                    if (element.order === 1) {
                        sorting += ' ASC';
                    } else if (element.order === -1) {
                        sorting += ' DESC';
                    }
                }
            }
        } else {
            if (table.sortField) {
                sorting = table.sortField;
                if (table.sortOrder === 1) {
                    sorting += ' ASC';
                } else if (table.sortOrder === -1) {
                    sorting += ' DESC';
                }
            }

        }

        return sorting;
    }

    getSortField(event: LazyLoadEvent): string {
        if (!event.sortField) {
            return '';
        }

        let sorting = event.sortField;

        if (event.sortOrder === 1) {
            sorting += ' ASC';
        } else if (event.sortOrder === -1) {
            sorting += ' DESC';
        }

        return sorting;
    }

    getFiltering(table: Table): string {
        return this.getFilterInternal(table.filters);
    }

    getFilter(event: LazyLoadEvent): string {
        return this.getFilterInternal(event.filters);
    }

    getFilterInternal(filters: FilterMetadata): string {
        const filter = Object.entries(filters).map(([k, v]) => this.toDynamicLinq(k, v['matchMode'], v['value'])).join(' and ');
        return filter;
    }

    changeTimeZone(date: Date, ianaTimezoneId: string): Date {
        let utcDateString = new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString();

        if (utcDateString.indexOf('T') < 0){
            throw Error('Invalid date format');
        }

        // construct a new DateTime from utcDateString
        let dateAndTimeParts = utcDateString.split('T');
        let dateParts = dateAndTimeParts[0].split('-');
        let timeParts = dateAndTimeParts[1].split('.')[0].split(':');

        let year = parseInt(dateParts[0]);
        let month = parseInt(dateParts[1]);
        let day = parseInt(dateParts[2]);

        let hour = parseInt(timeParts[0]);
        let minute = parseInt(timeParts[1]);
        let second = parseInt(timeParts[2]);

        return DateTime.fromObject({
            year: year,
            month: month,
            day: day,
            hour: hour,
            minute: minute,
            second: second
        }, { zone: ianaTimezoneId }).toJSDate();
    }

    toDynamicLinq(key, matchMode, value)  {
        const userTimeZone = abp.timing.timeZoneInfo.iana.timeZoneId;
        const valueAsDate = DateTime.fromJSDate(value, { zone: userTimeZone }).toUTC();
        const valueAsDateFromLocalStorage = DateTime.fromISO(value, { zone: userTimeZone }).toUTC();
        const nameContainsDate = (key.startsWith('date') || key.endsWith('Date'));
        const parseAsDate = nameContainsDate && valueAsDate.isValid && matchMode !== 'dateOnlyEq';
        const parseAsDateOnly = valueAsDate.isValid && matchMode === 'dateOnlyEq';

        const parseAsDateOnlyFromLocalStorage = nameContainsDate && !parseAsDate && !parseAsDateOnly && valueAsDateFromLocalStorage.isValid && matchMode === 'dateOnlyEq';
        const parseAsDateFromLocalStorage = nameContainsDate && !parseAsDate && !parseAsDateOnly && valueAsDateFromLocalStorage.isValid && matchMode !== 'dateOnlyEq';
        const parseAsNumber = !isNaN(+value) && ['is', 'lt', 'lte', 'gt', 'gte'].includes(matchMode);
        const isArray = Array.isArray(value);
        const isNumericArray = isArray && value.every(x => !isNaN(+x));
        const isDateArray = matchMode !== 'dateOnlyRange' && isArray && value.every(x => DateTime.fromJSDate(x, { zone: userTimeZone }).toUTC().isValid);
        const isDateOnlyArray = matchMode === 'dateOnlyRange' && isArray && value.every(x => DateTime.fromJSDate(x, { zone: userTimeZone }).toUTC().isValid);
        let valueEndRange: any;

        if (isArray === true) {
            if (isDateArray) {
                value = value
                    .map(x => DateTime.fromJSDate(x, { zone: userTimeZone }).toUTC())
                    .map(x => `DateTime(${x.toFormat('yyyy,LL,dd,HH,mm,ss')})`);
            } else if (isDateOnlyArray) {
                value = value
                .map(x => DateTime.fromJSDate(x, { zone: userTimeZone }).toUTC())
                .map(x => `"${x.toFormat('yyyy,LL,dd')}"`);
            } else if (isNumericArray) {
                value = value.join(',');
            } else {
                value = value.map(x => '"' + x + '"').join(',');
            }
        } else {
            if (parseAsDate) {
                value = `DateTime(${valueAsDate.toFormat('yyyy,LL,dd,HH,mm,ss')})`;
                valueEndRange = `DateTime(${valueAsDate.plus({ days: 1 }).toFormat('yyyy,LL,dd,HH,mm,ss')})`;
            } else if (parseAsDateFromLocalStorage) {
                let date = new Date(value);
                value = `DateTime(${DateTime.fromJSDate(date).toFormat('yyyy,LL,dd,HH,mm,ss')})`;
                valueEndRange = `DateTime(${DateTime.fromJSDate(date).plus({ days: 1 }).toFormat('yyyy,LL,dd,HH,mm,ss')})`;
            } else if (parseAsDateOnly) {
                value = `"${DateTime.fromJSDate(value).toFormat('yyyy,LL,dd')}"`;
            } else if (parseAsDateOnlyFromLocalStorage) {
                let date = this.changeTimeZone(new Date(value), abp.timing.timeZoneInfo.iana.timeZoneId);
                value = `"${DateTime.fromJSDate(date).toFormat('yyyy,LL,dd')}"`;
            }else if (!parseAsNumber) {
                value = '"' + value + '"';
            }
        }

        switch (matchMode) {
            case 'in': return `(${key} in (${value}))`;
            case 'contains': return `${key}.Contains(${value})`;
            case 'startsWith': return `${key}.StartsWith(${value})`;
            case 'endsWith': return `${key}.EndsWith(${value})`;
            case 'lt': return `${key}<${value}`;
            case 'lte': return `${key}<=${value}`;
            case 'gt': return `${key}>${value}`;
            case 'gte': return `${key}>=${value}`;
            case 'dateOnlyRange': // "DateTime(YYYY,MM,DD)"
                // parse, add difference, and format back to string
                const startDateOnly = value[0];
                const endDateOnly = value[1];

                const startDateOnlyPlusOne = `"${DateTime.fromFormat(startDateOnly.replace(/"/g, ''), 'yyyy,LL,dd').plus({ days: 1 }).toFormat('yyyy,LL,dd')}"`;
                const endDateOnlyPlusOne = `"${DateTime.fromFormat(endDateOnly.replace(/"/g, ''), 'yyyy,LL,dd').plus({ days: 1 }).toFormat('yyyy,LL,dd')}"`;
                return `${key} >= ${startDateOnlyPlusOne} and ${key} <= ${endDateOnlyPlusOne}`;
            case 'range': // "DateTime(YYYY,MM,DD,HH,MM,SS)"
                // parse, add difference, and format back to string
                const startStr = `DateTime(${this.parseToLuxon(value[0]).plus({ minutes: 59 }).toFormat('yyyy,LL,dd,HH,mm,ss')})`;
                const endStr = `DateTime(${this.parseToLuxon(value[1]).plus({ days: 1, minutes: 59 }).toFormat('yyyy,LL,dd,HH,mm,ss')})`;
                return `${key} >= ${startStr} and ${key} < ${endStr}`;
            case 'is': /* used for numeric equality, see parseAsNumber */
            case 'dateOnlyEq':
            case 'equals':
            default: return !(parseAsDate || parseAsDateFromLocalStorage) ? `${key}=${value}` : `${key}>=${value} and ${key}<${valueEndRange}`;
        }
    }

    filterDateIfValid(table: Table, textValue: string, model: Date, column: string, matchMode: string) {
        if (!textValue) {
            table.filter(null, column, matchMode);
            return;
        }

        const parsed = this.parseDate(textValue);
        // if (!parsed) {
        //     return;
        // }

        table.filter(parsed, column, matchMode);
    }

    parseToLuxon(str: string): DateTime {
        const parts = str.match(/\d+/g);
        if (!parts) {
            return DateTime.invalid('No date parts found');
        }

        const [year, month, day, hour, minute, second] = parts.map(Number);

        return DateTime.fromObject({
            year, month, day, hour, minute, second
        });
    }

    parseDate(input: string): Date | null {
        const regex = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/;

        const match = input.match(regex);
        if (!match) return null;

        let [_, d, m, y] = match;

        let day = Number(d);
        let month = Number(m) - 1; // JS months 0–11
        let year = Number(y);

        // Convert two digit years (00–99 --> 2000–2099)
        if (y.length === 2) {
            year = 2000 + year;
        }

        const date = new Date(year, month, day);

        return date;
    }

    hasFilter(table: Table): boolean {
        return Object.keys(table.filters).length > 0;
    }

    clearFilter(table: Table): void {
        this.filter = {};
        table.filters = {};
        table.filter(null, null, null);
    }

    getFilerValue(table: Table, key: string): string {
        let filter: FilterMetadata | FilterMetadata[] = table.filters[key];

        if (!Array.isArray(filter)) {
            return filter?.value ?? '';
        }

        return filter.length > 0 ? filter[0]?.value : '';
    }

    getMaxResultCount(paginator: Paginator, event: LazyLoadEvent): number {
        if (paginator && paginator.rows) {
            return paginator.rows;
        }

        if (!event || !event.rows) {
            return this.defaultRecordsCountPerPage;
        }

        return event.rows;
    }

    getSkipCount(paginator: Paginator, event: LazyLoadEvent): number {
        if (paginator && paginator.first) {
            return paginator.first;
        }

        if (!event || !event.first) {
            return 0;
        }

        return event.first;
    }

    shouldResetPaging(event: LazyLoadEvent): boolean {
        if (!event /*|| event.sortField*/) { // if you want to reset after sorting, comment out parameter
            return true;
        }

        return false;
    }

    adjustScroll(table: Table) {
        const rtl = rtlDetect.isRtlLang(abp.localization.currentLanguage.name);
        if (!rtl) {
            return;
        }

        const body: HTMLElement = table.el.nativeElement.querySelector('.ui-table-scrollable-body');
        const header: HTMLElement = table.el.nativeElement.querySelector('.ui-table-scrollable-header');
        body.addEventListener('scroll', () => {
          header.scrollLeft = body.scrollLeft;
        });
    }
}
